import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Plus, ClipboardList, Search } from 'lucide-react';
import ConditionScriptPicker from '@/components/ConditionScriptPicker';

/**
 * Inspection panels for the Appraisal Overview tab.
 *
 * Renders one collapsible card per inspection category (Roof Condition,
 * Ridging, Tiles, Sheeting, …). Each card has:
 *   - a textarea for free-text notes from the on-site inspection
 *   - an "Insert from Library" button that opens the existing
 *     ConditionScriptPicker scoped to that category (pre-filtered by the
 *     property's roof type)
 *
 * The composed text is stored on the appraisal under
 * `appraisal.inspection_comments[<category>]`. Saving the parent form
 * persists everything in a single PUT.
 */
export default function InspectionCommentsPanel({ api, value = {}, onChange, defaultRoofType = '', editing = false }) {
  const [categories, setCategories] = useState([]);
  const [openCard, setOpenCard] = useState({});
  const [pickerCategory, setPickerCategory] = useState(null);

  // Load the canonical list of categories once. Used in both edit and
  // read-only mode to render the cards in the agreed order.
  useEffect(() => {
    api.get('/condition-scripts/inspection-categories')
      .then(r => setCategories(Array.isArray(r.data) ? r.data.filter(c => c !== 'Appraisal Condition') : []))
      .catch(() => setCategories([]));
  }, [api]);

  const handleChange = useCallback((cat, text) => {
    onChange && onChange({ ...(value || {}), [cat]: text });
  }, [value, onChange]);

  const toggleOpen = (cat) => setOpenCard(o => ({ ...o, [cat]: !o[cat] }));

  const filled = (cat) => !!(value || {})[cat]?.trim();

  // In read-only mode only show categories that actually have content
  // so the Overview doesn't get cluttered with empty cards.
  const visibleCats = editing ? categories : categories.filter(filled);

  if (visibleCats.length === 0) {
    return editing ? (
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5 text-sm text-slate-500">
        <ClipboardList size={16} className="inline mr-2 text-slate-400" />
        No inspection categories configured yet.
      </div>
    ) : null;
  }

  return (
    <div data-testid="inspection-panels" className="bg-white border border-[#E2E2DF] rounded-md p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <ClipboardList size={14} className="text-[#D35400]" />
          Roof Inspection Comments
          <span className="text-[10px] font-mono-num text-slate-400 font-normal">({visibleCats.filter(filled).length}/{visibleCats.length} filled)</span>
        </h3>
        {editing && (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Search size={11} /> Roof type: <span className="font-bold text-[#D35400]">{defaultRoofType || 'all'}</span>
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {visibleCats.map(cat => {
          const isOpen = openCard[cat] !== false && (filled(cat) || editing);
          const text = (value || {})[cat] || '';
          return (
            <div key={cat} data-testid={`inspection-card-${cat.replace(/\s+/g, '-').toLowerCase()}`} className={`border rounded-md ${filled(cat) ? 'border-[#D35400]/40 bg-[#FFF7ED]/30' : 'border-[#E2E2DF]'}`}>
              <button
                type="button"
                onClick={() => toggleOpen(cat)}
                className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 transition-colors text-left"
              >
                {isOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                <span className="text-sm font-semibold text-slate-800 flex-1">{cat}</span>
                {filled(cat) && <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Filled</span>}
                {editing && (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`open-picker-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={(e) => { e.stopPropagation(); setPickerCategory(cat); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setPickerCategory(cat); } }}
                    className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#D35400] border border-[#D35400] rounded hover:bg-[#FFF7ED] cursor-pointer"
                  >
                    <Plus size={11} strokeWidth={2.5} /> Insert
                  </span>
                )}
              </button>
              {isOpen && (
                <div className="px-3 pb-3">
                  {editing ? (
                    <textarea
                      data-testid={`inspection-textarea-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                      value={text}
                      onChange={e => handleChange(cat, e.target.value)}
                      rows={Math.max(3, Math.min(8, text.split('\n').length + 1))}
                      placeholder={`On-site notes for ${cat}. Use "Insert" to pull standard phrases from the library.`}
                      className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-sans leading-relaxed bg-white"
                    />
                  ) : (
                    <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed bg-white border border-[#E2E2DF] rounded-md p-2.5">{text}</pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConditionScriptPicker
        open={!!pickerCategory}
        inspectionCategory={pickerCategory || ''}
        defaultRoofType={defaultRoofType}
        onClose={() => setPickerCategory(null)}
        onInsert={(text, mode) => {
          if (!pickerCategory) return;
          const existing = ((value || {})[pickerCategory] || '').trim();
          const next = mode === 'replace' || !existing ? text : `${existing}\n\n${text}`;
          handleChange(pickerCategory, next);
        }}
      />
    </div>
  );
}
